import { memo, useMemo, useState } from 'react';
import { Tabs, Text, useTheme } from '@Sage/hoi-poi-ui';
import Context from 'managers/Context';
import { UserChip } from 'containers/components/chips';
import { PATH_IMAGE } from 'constants/Environment';
import { getLiteral } from 'utils/getLiteral';
import '../styles.scss';

const EmptyList = ({ literal }) => {
    const theme = useTheme();
    return (
        <div className="empty-list">
            <div className="empty-list-image">
                <img src={`${PATH_IMAGE}img-empty-billboard.svg`} alt="" />
            </div>
            <Text type="body" as="div" color={theme.colors.utility.textSecondary}>
                {getLiteral(literal)}
            </Text>
        </div>
    );
};

const UserList = ({ groups, emptyLiteral }) => {
    const theme = useTheme();

    if (Context.utilsFormats.isEmptyObject(groups)) {
        return <EmptyList literal={emptyLiteral} />;
    }

    return Object.keys(groups).map((groupName) => (
        <div key={groupName} className="group-users">
            <Text
                type="subtitle"
                bold
                as="div"
                className="group-title"
                color={theme.colors.utility.textPrimary}
            >
                {groupName}
            </Text>
            <div className="users-list">
                {groups[groupName].map((user) => (
                    <div key={user.id} className="users-list-user">
                        <UserChip
                            idUser={user.id}
                            username={`${user.firstName} ${user.lastName}`}
                            remotePopover={true}
                        />
                        <Text type="caption" as="div" color={theme.colors.utility.textSecondary}>
                            {Context.utilsFormats.formatTimeStampDateLocal(user.actionDate)}
                        </Text>
                    </div>
                ))}
            </div>
        </div>
    ));
};

const Groups = ({
    groups,
    groupsCount,
    nonGroups,
    nonGroupsCount,
    hasTabs,
    tabsLiterals,
    emptyLiterals,
}) => {
    const [activeKey, setActiveKey] = useState('0');

    const tabs = useMemo(() => {
        if (!hasTabs) return [];
        return [
            {
                key: '0',
                title: `${tabsLiterals?.[0] ? getLiteral(tabsLiterals[0]) : ''} (${groupsCount})`,
                content: (
                    <div className="tab-scroll-content">
                        <UserList groups={groups} emptyLiteral={emptyLiterals?.[0]} />
                    </div>
                ),
            },
            {
                key: '1',
                title: `${tabsLiterals?.[1] ? getLiteral(tabsLiterals[1]) : ''} (${nonGroupsCount})`,
                content: (
                    <div className="tab-scroll-content">
                        <UserList groups={nonGroups} emptyLiteral={emptyLiterals?.[1]} />
                    </div>
                ),
            },
        ];
    }, [hasTabs, tabsLiterals, groupsCount, nonGroupsCount, groups, nonGroups, emptyLiterals]);

    if (hasTabs) {
        return (
            <div className="groups-users groups-users--tabbed">
                <Tabs tabs={tabs} activeKey={activeKey} onChange={setActiveKey} />
            </div>
        );
    }

    return (
        <div className="groups-users">
            <div className="tab-scroll-content">
                <UserList groups={groups} emptyLiteral={emptyLiterals?.[0]} />
            </div>
        </div>
    );
};

export default memo(Groups);
